import { motion } from 'framer-motion'
import { useScroll, useTransform } from 'framer-motion'
import { SpriteGenerator } from './sprite-generator'
import React from 'react'

const sample_sprites = [ ]

const features = [
  {
    name: 'Instant Generation',
    description: 'Generate sprites in milliseconds with our powerful API.',
    icon: '🚀',
  },
  {
    name: 'Customizable',
    description: 'Full control over style, size, and appearance of your sprites.',
    icon: '⚙️',
  },
  {
    name: 'High Quality',
    description: 'Beautiful, crisp sprites perfect for games and applications.',
    icon: '✨',
  },
  {
    name: 'Easy Integration',
    description: 'Simple REST API with comprehensive documentation.',
    icon: '🔌',
  },
]

interface FloatingSpriteProps {
  className?: string;
  delay?: number;
  speed?: number;
  spriteUrl: string;
}

const FloatingSprite = ({ 
  className = '', 
  delay = 0, 
  speed = 1, 
  spriteUrl 
}: FloatingSpriteProps) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, speed * 200]);

  return (
    <motion.div
      className={`absolute ${className} cursor-pointer`}
      animate={{
        y: [0, -20, 0],
        rotate: [0, 5, -5, 0],
      }}
      whileHover={{
        scale: 1.2,
        filter: "brightness(1.2)",
        transition: { duration: 0.2 }
      }}
      transition={{
        duration: 5,
        repeat: Infinity,
        delay,
      }}
      style={{ y }}
    >
      <div className="w-24 h-24 relative">
        <img
          src={spriteUrl}
          alt="Floating sprite"
          className="object-contain drop-shadow-lg"
          sizes="(max-width: 768px) 96px, 96px"
          loading="eager"
        />
      </div>
    </motion.div>
  );
}

export default function Home() {
  return (
    <div className="relative isolate">
      {/* Background decorative elements */}
      <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
        <div
          className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>

      {/* Animated sprites */}

      {/* Hero section */}
      <div className="relative px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-2xl py-32 sm:py-48 lg:py-56">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
            >
              Generate Beautiful Sprites with AI
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-lg leading-8 text-gray-600"
            >
              Transform your ideas into stunning sprites instantly. Perfect for game developers,
              designers, and creative professionals.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-10"
            >
              <SpriteGenerator />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-24">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base font-semibold leading-7 text-indigo-600">Powerful Features</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Everything you need to create amazing sprites
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-4">
            {features.map((feature) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex flex-col"
              >
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  <span className="text-2xl">{feature.icon}</span>
                  {feature.name}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">{feature.description}</p>
                </dd>
              </motion.div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  )
}
